import React from "react";
import {
  Box,
  Button,
  CopyToClipboard,
  Link,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-react";

// Entry point to the TrackStars AI analyses: open the dashboard, or run one.

export type TrackStarsProduct = "dlq" | "shadow" | "oms";

const TRACKSTARS_DASHBOARD_URL = "https://trackstars.prime-video.amazon.dev";

// The dashboard routes off window.location.hash, so these must match its router.
const TRACKSTARS_ROUTES: Record<TrackStarsProduct, string> = {
  dlq: `${TRACKSTARS_DASHBOARD_URL}/#dlq`,
  shadow: `${TRACKSTARS_DASHBOARD_URL}/#shadow`,
  // Default to the accountant view; the engineer view is one click away.
  oms: `${TRACKSTARS_DASHBOARD_URL}/#oms/accountant`,
};

// Run from a TrackStars Brazil workspace on a Cloud Desktop.
const COMMANDS: Record<TrackStarsProduct, string> = {
  dlq: "brazil-runtime-exec python -m track_stars.main -d 14 --publish",
  shadow:
    "brazil-runtime-exec python -m track_stars.main --shadow-test --publish",
  oms:
    "brazil-runtime-exec python -m track_stars.main --oms " +
    "--oms-ticket TRACK-XXXX --oms-issue-id NNNN --publish --output-dir /tmp/oms",
};

const CLAUDE_PROMPTS: Record<TrackStarsProduct, string> = {
  dlq:
    'claude --dangerously-skip-permissions "Using the dlq-analysis skill in ' +
    "TrackStarsDevDocs, run a DLQ analysis for the last 14 days and publish it " +
    'to the dashboard."',
  shadow:
    'claude --dangerously-skip-permissions "Using TrackStarsDevDocs, run the ' +
    'TrackStars shadow test variance analysis and publish it to the dashboard."',
  oms:
    'claude --dangerously-skip-permissions "Using the oms-investigation skill in ' +
    "TrackStarsDevDocs, investigate OMS ticket TRACK-XXXX issue NNNN and publish " +
    'it to the dashboard."',
};

const DOCS: Record<TrackStarsProduct, string> = {
  dlq: "https://code.amazon.com/packages/TrackStarsDevDocs/blobs/mainline/--/.kiro/skills/dlq-analysis/SKILL.md",
  shadow:
    "https://code.amazon.com/packages/TrackStarsDevDocs/blobs/mainline/--/.kiro/skills",
  oms: "https://code.amazon.com/packages/TrackStarsDevDocs/blobs/mainline/--/.kiro/skills/oms-investigation/SKILL.md",
};

const LABELS: Record<TrackStarsProduct, { title: string; blurb: string }> = {
  dlq: {
    title: "TrackStars DLQ analysis",
    blurb:
      "Finds patterns in uncategorized DLQ failures so they can be categorized and resolved. Publishes the analysis to the TrackStars dashboard.",
  },
  shadow: {
    title: "TrackStars shadow test analysis",
    blurb:
      "Finds patterns in unclassified prod-vs-gamma discrepancies so they can be classified and fixed proactively. Publishes to the TrackStars dashboard.",
  },
  oms: {
    title: "TrackStars OMS investigation",
    blurb:
      "Investigates why an aged balance is open: sizes the population, groups it by cause, and gives per-family guidance. Publishes to the TrackStars dashboard.",
  },
};

interface Props {
  product: TrackStarsProduct;
  /** Override the deep-link if the caller wants a specific report or view. */
  routeOverride?: string;
}

function CommandBlock({ label, command }: { label: string; command: string }) {
  return (
    <div>
      {/* Copy sits by the label so it cannot overlap a long single-line command. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box variant="awsui-key-label">{label}</Box>
        <CopyToClipboard
          copyButtonAriaLabel="Copy"
          copyErrorText="Failed to copy"
          copySuccessText="Copied"
          textToCopy={command}
          variant="icon"
        />
      </div>
      <Box
        variant="code"
        fontSize="body-s"
        color="text-status-inactive"
        padding="xs"
      >
        <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
          {command}
        </span>
      </Box>
    </div>
  );
}

export default function TrackStarsLaunch({ product, routeOverride }: Props) {
  const href = routeOverride ?? TRACKSTARS_ROUTES[product];
  const { title, blurb } = LABELS[product];

  return (
    <Popover
      dismissButton
      position="bottom"
      size="large"
      triggerType="custom"
      header={title}
      content={
        // Fixed width so long commands wrap instead of stretching the popover.
        <div style={{ width: 460, maxWidth: "100%" }}>
          <SpaceBetween size="m">
            <Box color="text-body-secondary">{blurb}</Box>
            <Button
              iconName="external"
              href={href}
              target="_blank"
              variant="primary"
            >
              Open dashboard
            </Button>
            <CommandBlock
              label="Run it yourself on Cloud Desktop"
              command={COMMANDS[product]}
            />
            <CommandBlock
              label="Or ask Claude to run it"
              command={CLAUDE_PROMPTS[product]}
            />
            <Link external href={DOCS[product]}>
              How this works (dev docs)
            </Link>
          </SpaceBetween>
        </div>
      }
    >
      <Button iconName="gen-ai" variant="normal">
        TrackStars AI
      </Button>
    </Popover>
  );
}
